"use client";

import { useEffect, useRef } from "react";
import { BUTTON_SMALL } from "@/components/tool-kit/styles";
import { type CvdType, simulatePixels } from "@/lib/tool-kit/color-charts/cvd";
import { downloadBlob } from "@/lib/tool-kit/download";

export interface SourceImage {
  width: number;
  height: number;
  /** RGBA bytes, sRGB, as `ImageData.data`. */
  data: Uint8ClampedArray;
  name: string;
}

/** Draw `pixels` (RGBA, `width` × `height`) on a canvas. */
function paint(
  canvas: HTMLCanvasElement,
  pixels: Uint8ClampedArray,
  width: number,
  height: number
) {
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) {
    return;
  }
  const image = context.createImageData(width, height);
  image.data.set(pixels);
  context.putImageData(image, 0, 0);
}

/**
 * One panel of the side-by-side view: the picture as a simulated viewer would see it, on
 * a canvas, with its own PNG download. The pixels never leave the page — the canvas is
 * only ever read by the Download button and the sheet export.
 */
export function CvdCanvas({
  source,
  type,
  severity,
  label,
  ariaLabel,
  downloadLabel,
  filename,
  id,
  register,
}: {
  source: SourceImage;
  /** Null draws the original. */
  type: CvdType | null;
  /** 0–1. */
  severity: number;
  label: string;
  ariaLabel: string;
  downloadLabel: string;
  filename: string;
  /** Key the parent files this canvas under (for the sheet export). */
  id: string;
  /** Must be stable between renders, or the canvas is repainted every time. */
  register: (id: string, canvas: HTMLCanvasElement | null) => void;
}) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) {
      return;
    }
    const pixels = type
      ? simulatePixels(source.data, type, severity)
      : source.data;
    paint(canvas, pixels, source.width, source.height);
    register(id, canvas);
    return () => register(id, null);
  }, [source, type, severity, id, register]);

  function save() {
    ref.current?.toBlob((blob) => {
      if (blob) {
        downloadBlob(filename, blob);
      }
    }, "image/png");
  }

  return (
    <figure className="space-y-2">
      <figcaption className="font-medium text-foreground text-sm">
        {label}
      </figcaption>
      <canvas
        aria-label={ariaLabel}
        className="h-auto w-full rounded-lg border border-border bg-white"
        ref={ref}
        role="img"
      />
      <button className={BUTTON_SMALL} onClick={save} type="button">
        {downloadLabel}
      </button>
    </figure>
  );
}

/**
 * Join the original and the shown simulations into one labelled PNG: a grid two panels
 * wide on white, each panel under its caption.
 */
export function exportSheet(
  panels: readonly { label: string; canvas: HTMLCanvasElement }[],
  fontFamily: string
): Promise<Blob | null> {
  const first = panels[0]?.canvas;
  if (!first) {
    return Promise.resolve(null);
  }
  const gap = 16;
  const caption = 28;
  const columns = Math.min(2, panels.length);
  const rows = Math.ceil(panels.length / columns);
  const cellW = first.width;
  const cellH = first.height + caption;
  const sheet = document.createElement("canvas");
  sheet.width = columns * cellW + (columns + 1) * gap;
  sheet.height = rows * cellH + (rows + 1) * gap;
  const context = sheet.getContext("2d");
  if (!context) {
    return Promise.resolve(null);
  }
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, sheet.width, sheet.height);
  context.fillStyle = "#1f1f1f";
  context.font = `bold 15px ${fontFamily}`;
  for (const [index, panel] of panels.entries()) {
    const x = gap + (index % columns) * (cellW + gap);
    const y = gap + Math.floor(index / columns) * (cellH + gap);
    context.fillText(panel.label, x, y + 18, cellW);
    context.drawImage(panel.canvas, x, y + caption);
  }
  return new Promise((resolve) => sheet.toBlob(resolve, "image/png"));
}
